import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDTypography from "components/MDTypography"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import StatusBadge from "./components/StatusBadge"
import SunPaymentBadge from "./components/SunPaymentBadge"
import StatusStepper from "./components/StatusStepper"
import SectionCard from "./components/SectionCard"
import DetailTab from "./tabs/DetailTab"
import RelatedApprovalTab from "./tabs/RelatedApprovalTab"
import GrnTab from "./tabs/GrnTab"
import {
  useGetProcurementRequestQuery,
  useSubmitProcurementRequestMutation,
  useResubmitProcurementRequestMutation,
  useMarkPaymentReadyMutation,
  useDeleteProcurementRequestMutation,
} from "shared/redux/endpoints/procurement"
import { useSetPrApprovalDecisionMutation } from "shared/redux/endpoints/procurementV2"
import {
  selectDetailActiveTab,
  setDetailActiveTab,
} from "shared/redux/slices/procurementSlice"
import { requesterActions, approverActions, PR_STATUS } from "./status"
import { showToastSuccess, showToastError } from "utilities/toastUtil"

const TABS = [
  { key: "detail", label: "Detail" },
  { key: "approval", label: "Related Approval" },
  { key: "grn", label: "GRN" },
]

const TabButton = ({ active, label, onClick }) => (
  <MDButton
    size="small"
    variant={active ? "gradient" : "text"}
    color="info"
    onClick={onClick}
  >
    {label}
  </MDButton>
)

const RequestDetailContainer = ({ manager = false }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const activeTab = useSelector(selectDetailActiveTab)
  const [modal, setModal] = useState(null) // "reject" | "info" | null
  const [reason, setReason] = useState("")

  const { data: request, isFetching } = useGetProcurementRequestQuery(id, {
    skip: !id,
  })
  const [submit, { isLoading: submitting }] = useSubmitProcurementRequestMutation()
  const [resubmit, { isLoading: resubmitting }] = useResubmitProcurementRequestMutation()
  const [markPaymentReady, { isLoading: marking }] = useMarkPaymentReadyMutation()
  const [deleteRequest, { isLoading: deleting }] = useDeleteProcurementRequestMutation()
  const [decide, { isLoading: deciding }] = useSetPrApprovalDecisionMutation()

  const status = request?.status
  const actions = requesterActions(status)
  // Manager approval actions only when this manager is the assigned approver
  // and the PR is awaiting a decision.
  const mgrActions = approverActions(
    status,
    manager && !!request?.assignedApproverIsMe
  )

  const applyDecision = async (decision) => {
    try {
      await decide({ id, decision, reason }).unwrap()
      showToastSuccess("Your decision has been recorded.")
      setModal(null)
      setReason("")
      refetch?.()
    } catch (e) {
      const msg = e?.data?.data?.message || e?.data?.message || "Failed to submit your decision."
      showToastError(msg)
    }
  }

  if (isFetching) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <MDTypography>Loading…</MDTypography>
        </MDBox>
      </DashboardLayout>
    )
  }

  if (!request) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <MDTypography color="error">Request not found.</MDTypography>
        </MDBox>
      </DashboardLayout>
    )
  }

  const handleDelete = async () => {
    try {
      await deleteRequest(id).unwrap()
      showToastSuccess("Request deleted successfully.")
      navigate("/procurement/requests")
    } catch (e) {
      const msg = e?.data?.data?.message || e?.data?.message || "Failed to delete this request."
      showToastError(msg)
    }
  }

  return (
    <DashboardLayout>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <MDBox>
            <MDTypography variant="h4">{request.prCode || `PR #${id}`}</MDTypography>
            <MDTypography variant="caption" color="text">
              Procurement › Purchase Requisition › Detail
            </MDTypography>
          </MDBox>
          <MDBox display="flex" gap={1} alignItems="center">
            <StatusBadge status={status} />
            <SunPaymentBadge status={request.sunPaymentStatus} />
          </MDBox>
        </MDBox>

        <MDBox mb={3}>
          <StatusStepper status={status} />
        </MDBox>

        {(mgrActions.canApprove ||
          mgrActions.canReject ||
          mgrActions.canInfoRequest) && (
          <SectionCard
            title="Approval"
            subtitle="You are the assigned approver for this PR."
            icon="how_to_reg"
            p={2.5}
            mb={3}
          >
            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton
                variant="gradient"
                color="success"
                disabled={deciding}
                onClick={() => applyDecision("approved")}
              >
                Approve
              </MDButton>
              <MDButton
                variant="outlined"
                color="error"
                disabled={deciding}
                onClick={() => setModal("reject")}
              >
                Reject
              </MDButton>
              <MDButton
                variant="outlined"
                color="warning"
                disabled={deciding}
                onClick={() => setModal("info")}
              >
                Info Request
              </MDButton>
            </MDBox>
          </SectionCard>
        )}

        {/* Requester action bar (§7.3) */}
        <MDBox display="flex" gap={1} mb={2} flexWrap="wrap">
          {actions.canEdit && (
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              onClick={() => navigate(`/procurement/requests/${id}/edit`)}
            >
              Edit
            </MDButton>
          )}
          {actions.canSubmit && (
            <MDButton
              size="small"
              variant="gradient"
              color="info"
              disabled={submitting}
              onClick={() => submit(id)}
            >
              Submit for Approval
            </MDButton>
          )}
          {actions.canResubmit && (
            <MDButton
              size="small"
              variant="gradient"
              color="info"
              disabled={resubmitting}
              onClick={() => resubmit(id)}
            >
              Resubmit
            </MDButton>
          )}
          {actions.canPaymentReady && (
            <MDButton
              size="small"
              variant="gradient"
              color="success"
              disabled={marking}
              onClick={() => markPaymentReady(id)}
            >
              Payment Ready
            </MDButton>
          )}
          {actions.canExport && (
            <MDButton size="small" variant="outlined" color="secondary">
              Export PR
            </MDButton>
          )}
          {actions.canDelete && (
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              disabled={deleting}
              onClick={handleDelete}
            >
              Delete
            </MDButton>
          )}
        </MDBox>

        {/* Tabs */}
        <MDBox display="flex" gap={1} mb={2}>
          {TABS.map((t) => (
            <TabButton
              key={t.key}
              active={activeTab === t.key}
              label={t.label}
              onClick={() => dispatch(setDetailActiveTab(t.key))}
            />
          ))}
        </MDBox>

        {activeTab === "detail" && <DetailTab request={request} />}
        {activeTab === "approval" && <RelatedApprovalTab requestId={id} />}
        {activeTab === "grn" && (
          <GrnTab requestId={id} readOnly={!actions.canUploadDocs && status !== "approved"} />
        )}
      </MDBox>

      <Dialog open={!!modal} onClose={() => setModal(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {modal === "reject" ? "Reject PR" : "Request more info"}
        </DialogTitle>
        <DialogContent>
          <MDBox pt={1}>
            <MDInput
              fullWidth
              multiline
              rows={3}
              labelType="non-sticky"
              labelText="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setModal(null)}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color={modal === "reject" ? "error" : "warning"}
            disabled={!reason.trim() || deciding}
            onClick={() =>
              applyDecision(modal === "reject" ? PR_STATUS.REJECTED : PR_STATUS.INFO_REQUESTED)
            }
          >
            {modal === "reject" ? "Confirm Reject" : "Send Info Request"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  )
}

export default RequestDetailContainer
